import { apiClient } from '../lib/apiClient';

export interface Department {
  id: string;
  org_id: string;
  name: string;
  description: string;
  manager_id: string | null;
  parent_department_id: string | null;
  is_active: boolean;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface DepartmentMember {
  id: string;
  org_id: string;
  user_id: string;
  department_id: string;
  role: 'manager' | 'member';
  added_by: string;
  added_at: string;
  created_at: string;
}

export interface DepartmentAccess {
  canAccessAll: boolean;
  accessibleDepartmentIds: string[];
  isDepartmentOwner: boolean;
  ownedDepartmentIds: string[];
}

class DepartmentFilterService {
  async getUserDepartmentAccess(userId: string, orgId: string): Promise<DepartmentAccess> {
    return apiClient.get<DepartmentAccess>(
      `/orgs/${orgId}/department-access?userId=${userId}`
    );
  }

  filterByDepartments<T extends { department_id?: string | null }>(
    items: T[],
    departmentAccess: DepartmentAccess
  ): T[] {
    if (departmentAccess.canAccessAll) {
      return items;
    }
    return items.filter(item => {
      if (!item.department_id) return true;
      return departmentAccess.accessibleDepartmentIds.includes(item.department_id);
    });
  }

  canAccessDepartment(departmentId: string | null | undefined, departmentAccess: DepartmentAccess): boolean {
    if (departmentAccess.canAccessAll) return true;
    if (!departmentId) return true;
    return departmentAccess.accessibleDepartmentIds.includes(departmentId);
  }

  async getDepartmentOwnerInfo(departmentId: string): Promise<{ id: string; display_name: string; email: string } | null> {
    return apiClient.get<{ id: string; display_name: string; email: string } | null>(
      `/departments/${departmentId}/owner`
    ).catch(() => null);
  }

  async getAllDepartments(orgId: string): Promise<Department[]> {
    return apiClient.get<Department[]>(`/orgs/${orgId}/departments`);
  }

  async getDepartmentMembers(departmentId: string): Promise<DepartmentMember[]> {
    return apiClient.get<DepartmentMember[]>(`/departments/${departmentId}/members`);
  }

  async addDepartmentMember(
    orgId: string,
    departmentId: string,
    userId: string,
    role: 'manager' | 'member',
    addedBy: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      await apiClient.post(`/departments/${departmentId}/members`, { orgId, userId, role, addedBy });
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err?.message };
    }
  }

  async removeDepartmentMember(departmentId: string, userId: string): Promise<{ success: boolean; error?: string }> {
    try {
      await apiClient.delete(`/departments/${departmentId}/members/${userId}`);
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err?.message };
    }
  }

  async updateDepartmentOwner(departmentId: string, managerId: string | null): Promise<{ success: boolean; error?: string }> {
    try {
      await apiClient.patch(`/departments/${departmentId}`, { manager_id: managerId });
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err?.message };
    }
  }

  async createDepartment(
    orgId: string,
    name: string,
    description: string,
    managerId: string | null,
    parentDepartmentId: string | null
  ): Promise<{ success: boolean; department?: Department; error?: string }> {
    try {
      const department = await apiClient.post<Department>(`/orgs/${orgId}/departments`, {
        name,
        description,
        manager_id: managerId,
        parent_department_id: parentDepartmentId,
        is_active: true,
      });
      return { success: true, department };
    } catch (err: any) {
      return { success: false, error: err?.message };
    }
  }

  async updateDepartment(
    departmentId: string,
    updates: Partial<Pick<Department, 'name' | 'description' | 'manager_id' | 'parent_department_id' | 'is_active'>>
  ): Promise<{ success: boolean; error?: string }> {
    try {
      await apiClient.patch(`/departments/${departmentId}`, updates);
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err?.message };
    }
  }
}

export const departmentFilterService = new DepartmentFilterService();
